import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Bot, Save, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import MobileNav from '@/components/MobileNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminZombiebotPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchKnowledgeBase();
  }, []);

  const fetchKnowledgeBase = async () => {
    try {
      const response = await axios.get(`${API}/bot/kb`);
      setContent(response.data.content || '');
      setOriginalContent(response.data.content || '');
      setLastUpdated(response.data.updated_at);
    } catch (error) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bot/kb`, { content });
      setOriginalContent(content);
      setLastUpdated(new Date().toISOString());
      toast.success('Knowledge base saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = content !== originalContent;

  return (
    <div className="min-h-screen bg-black pb-20" data-testid="admin-zombiebot-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="text-white p-2 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-lg font-bold text-white flex items-center gap-2">
            <Bot className="w-5 h-5 text-neon-cyan" />
            ZOMBIEBOT
          </h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Info */}
        <div className="bg-neon-cyan/10 border border-neon-cyan/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-neon-cyan shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-300">
              <p className="font-medium text-neon-cyan mb-1">Knowledge Base</p>
              <p>This content is injected into Zombiebot's prompt. Use it to give the bot context about your project, characters, lore, rules, etc.</p>
            </div>
          </div>
        </div>

        {/* Editor */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-neon-pink border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-zinc-400 text-sm">
                Content ({content.length}/10000 chars)
              </label>
              {lastUpdated && (
                <span className="text-zinc-600 text-xs">
                  Last saved: {new Date(lastUpdated).toLocaleString()}
                </span>
              )}
            </div>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter knowledge base content...

Example:
- The zombie apocalypse started in 2025
- Main characters: Alice (survivor), Bob (scientist)
- Safe zones: New York bunker, LA fortress
- Zombie types: walkers, runners, screamers"
              className="bg-zinc-900 border-zinc-700 text-white min-h-[400px] font-mono text-sm"
              maxLength={10000}
              data-testid="kb-textarea"
            />
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="w-full bg-neon-pink hover:bg-neon-pink/80"
              data-testid="save-kb-btn"
            >
              {saving ? (
                'Saving...'
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {hasChanges ? 'Save Changes' : 'No Changes'}
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};

export default AdminZombiebotPage;
